import * as THREE from 'three';
import { SKY, WORLD } from '../config/tuning';
import { SKY_FRAGMENT, SKY_VERTEX } from '../render/shaders/sky.glsl';
import { clamp01, lerp, smoothstep } from './generators/Noise';

/**
 * Sky dome, sun, and the lighting everything else is lit by (SPEC §3.9.3).
 *
 * Time of day is a mission parameter and a genuine tactical variable, so this is not decoration:
 * the sun direction it produces is what makes a dawn easterly ingress blind the camera, and it is
 * the same vector the ocean's specular and the terrain's shading read. One source of truth for
 * where the light comes from.
 *
 * Angles in radians internally; hours and degrees only at the config boundary.
 */

const DEG_TO_RAD = Math.PI / 180;

export class Sky {
  readonly object3d: THREE.Mesh;

  /** Unit vector *toward* the sun. */
  readonly sunDirection = new THREE.Vector3();
  readonly sunColour = new THREE.Color();
  readonly horizonColour = new THREE.Color();
  readonly zenithColour = new THREE.Color();

  readonly sunLight: THREE.DirectionalLight;
  readonly ambientLight: THREE.HemisphereLight;

  /** Fog colour to match the horizon; read by the terrain and ocean materials. */
  readonly hazeColour = new THREE.Color();
  hazeDensity: number = SKY.hazeDensity;

  private readonly material: THREE.ShaderMaterial;
  private readonly geometry: THREE.SphereGeometry;
  private timeOfDayHours: number = SKY.defaultTimeOfDayHours;

  constructor() {
    this.geometry = new THREE.SphereGeometry(1, 32, 16);
    this.material = new THREE.ShaderMaterial({
      vertexShader: SKY_VERTEX,
      fragmentShader: SKY_FRAGMENT,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      uniforms: {
        uSunDirection: { value: this.sunDirection },
        uZenithColour: { value: this.zenithColour },
        uHorizonColour: { value: this.horizonColour },
        uSunColour: { value: this.sunColour },
        uSunIntensity: { value: 1 },
        uHazeThickness: { value: 1 },
      },
    });

    this.object3d = new THREE.Mesh(this.geometry, this.material);
    this.object3d.frustumCulled = false;
    // Drawn first, with depth off, so the dome never fights the terrain for the far plane.
    this.object3d.renderOrder = -1000;

    this.sunLight = new THREE.DirectionalLight(0xffffff, 1);
    this.sunLight.castShadow = false;
    this.ambientLight = new THREE.HemisphereLight(0xffffff, 0x404040, 1);

    this.setTimeOfDay(SKY.defaultTimeOfDayHours);
  }

  get hours(): number {
    return this.timeOfDayHours;
  }

  /** Sun elevation above the horizon, radians. Negative after sunset. */
  get sunElevationRad(): number {
    return Math.asin(clamp01(this.sunDirection.y) * Math.sign(this.sunDirection.y) || 0);
  }

  /**
   * Set the time of day, 0–24 hours. Sunrise at 6, noon at 12, sunset at 18; the sun tracks
   * east → south → west, so an easterly heading at dawn puts it straight down the camera.
   */
  setTimeOfDay(hours: number): void {
    this.timeOfDayHours = ((hours % 24) + 24) % 24;

    // Day fraction from sunrise to sunset, −ve before dawn and after dusk.
    const dayFraction = (this.timeOfDayHours - SKY.sunriseHours) / (SKY.sunsetHours - SKY.sunriseHours);
    const elevationRad = Math.sin(dayFraction * Math.PI) * SKY.maxSunElevationDeg * DEG_TO_RAD;
    const azimuthRad = lerp(SKY.sunriseAzimuthDeg, SKY.sunsetAzimuthDeg, clamp01(dayFraction)) * DEG_TO_RAD;

    const horizontal = Math.cos(elevationRad);
    this.sunDirection
      .set(Math.sin(azimuthRad) * horizontal, Math.sin(elevationRad), -Math.cos(azimuthRad) * horizontal)
      .normalize();

    // Colour by elevation: warm and dim near the horizon, neutral and bright overhead.
    const altitude = clamp01(Math.sin(elevationRad) / Math.sin(SKY.maxSunElevationDeg * DEG_TO_RAD));
    const daylight = smoothstep(-0.06, 0.16, Math.sin(elevationRad));

    this.sunColour.setRGB(
      lerp(1.0, 1.0, altitude),
      lerp(0.62, 0.95, altitude),
      lerp(0.36, 0.88, altitude),
    );
    this.zenithColour.setRGB(
      lerp(0.06, 0.16, altitude) * lerp(0.25, 1, daylight),
      lerp(0.11, 0.34, altitude) * lerp(0.28, 1, daylight),
      lerp(0.2, 0.58, altitude) * lerp(0.4, 1, daylight),
    );
    this.horizonColour.setRGB(
      lerp(0.52, 0.6, altitude) * lerp(0.2, 1, daylight),
      lerp(0.42, 0.7, altitude) * lerp(0.24, 1, daylight),
      lerp(0.38, 0.76, altitude) * lerp(0.34, 1, daylight),
    );

    const intensity = lerp(SKY.nightSunIntensity, SKY.daySunIntensity, daylight);
    this.material.uniforms['uSunIntensity'].value = intensity;
    this.material.uniforms['uHazeThickness'].value = lerp(1.9, 1.0, altitude);

    this.sunLight.position.copy(this.sunDirection).multiplyScalar(WORLD.sizeM);
    this.sunLight.color.copy(this.sunColour);
    this.sunLight.intensity = lerp(0.05, SKY.sunLightIntensity, daylight);

    this.ambientLight.color.copy(this.horizonColour).multiplyScalar(1.6);
    this.ambientLight.groundColor.setRGB(0.09, 0.11, 0.12);
    this.ambientLight.intensity = lerp(0.12, SKY.ambientIntensity, daylight);

    // Haze takes the horizon colour so distant terrain dissolves into the sky rather than into
    // an arbitrary grey — which is what makes a coastline at 10 km read as distance.
    this.hazeColour.copy(this.horizonColour).multiplyScalar(1.05);
    this.hazeDensity = lerp(SKY.hazeDensity * 1.35, SKY.hazeDensity, daylight);
  }

  /** Keep the dome centred on the camera; it has no position of its own. */
  follow(cameraPosition: THREE.Vector3): void {
    this.object3d.position.copy(cameraPosition);
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
